import CredentialWizardContent from '@/components/credentials/credentialWizard/CredentialWizardContent';
import clsx from '@/lib/clsx';
import { getApps, getCredentialById } from '@/redux/actions/gravityActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import argumentDataSources from '@/schema/argumentDataSources.js';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isPublishableApp } from 'shared/utils/isPublishableApp.js';

const CredentialWizard = forwardRef(({ credentialId, onClose, className = '' }, ref) => {
  const [loading, setLoading] = useState(false);
  const [initialSecrets, setInitialSecrets] = useState(null);

  const companyId = useSelector(selectors.companyId);
  const credential = useArgSelector(selectors.credentialById, credentialId);
  const appVersion = useSelector((state) => {
    const appMetadata = selectors.appsMetadataById(state, credential?.appId);
    return isPublishableApp(appMetadata?.slug) ? appMetadata?.publishedVersion || appMetadata?.version || 'draft' : 'draft';
  });
  const app = useSelector((state) => selectors.appById(state, credential?.appId, appVersion));
  const canEditCredentials = useArgSelector(selectors.permission, 'credential:edit');

  const dataSource = useMemo(() => {
    if (window.location.pathname.includes('/workflows')) {
      return argumentDataSources.workflowStep;
    }
    if (window.location.pathname.includes('/credentials')) {
      return argumentDataSources.credentialEditPage;
    }
    return argumentDataSources.home;
  }, [window.location.pathname]);

  useEffect(() => {
    const loadCredential = async () => {
      if (credentialId) {
        setLoading(true);
        await getCredentialById(credentialId, { secrets: true, companyId }, () => setLoading(false));
      }
    };

    loadCredential();
  }, [credentialId]);

  useEffect(() => {
    if (credential?.appId && appVersion) {
      getApps({ versions: JSON.stringify([{ appId: credential?.appId, appVersion }]) });
    }
  }, [credential?.appId, appVersion]);

  useEffect(() => {
    const cleanSecrets = { ...credential?.secrets, label: undefined, custom: undefined };
    if (cleanSecrets && JSON.stringify(cleanSecrets) !== JSON.stringify(initialSecrets)) {
      setInitialSecrets(cleanSecrets);
    }
  }, [JSON.stringify(credential?.secrets)]);

  useEffect(() => {
    const body = document.querySelector('.credential-wizard .comp-wizard-body');
    if (!body) {
      return;
    }
    const handleScroll = () => {
      body.classList.toggle('has-scrolled', body.scrollTop > 0);
    };
    body.addEventListener('scroll', handleScroll);
    return () => {
      body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={clsx('credential-wizard', className)}>
      <CredentialWizardContent ref={ref} app={app} companyId={companyId} credentialId={credentialId} credential={credential} initialSecrets={initialSecrets} canEditCredentials={canEditCredentials} dataSource={dataSource} loading={loading} onClose={onClose} />
    </div>
  );
});

CredentialWizard.displayName = 'CredentialWizard';

export default CredentialWizard;
